import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import type { IAiSearchResponse } from '@/services/aiSearch/client';
import { useGrowthBookFeature } from '@/services/experiments/growthbook';

const FLAG = 'rv-ai-search';
const STORAGE_KEY = 'od_rv_ai_search_enabled';
const FORCE_KEY = 'od_rv_ai_search_force';
const LAST_SEARCH_KEY = 'od_rv_ai_search_last';

/** The latest AI search in this session: drives the results heading and the header placeholder. */
interface IAiLastSearch {
  query: string;
  /** Vibe slugs in effect; they aren't URL filters, so they're carried here. */
  vibes: string[];
}

interface IAiSearchContext {
  /** Flag on (or dev-forced), on an AI-search route, and past hydration. */
  available: boolean;
  /** Flag on (or dev-forced), on the homepage, and past hydration: show the AI hero. */
  homeHeroAvailable: boolean;
  /** Renter opted in via the top-bar toggle or a homepage AI search (persisted). */
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  /** Null until an AI search runs; cleared when the homepage hero mounts (a fresh start). */
  lastSearch: IAiLastSearch | null;
  recordSearch: (query: string, resp: IAiSearchResponse) => void;
  clearLastSearch: () => void;
}

const AiSearchContext = createContext<IAiSearchContext>({
  available: false,
  homeHeroAvailable: false,
  enabled: false,
  setEnabled: () => undefined,
  lastSearch: null,
  recordSearch: () => undefined,
  clearLastSearch: () => undefined,
});

export const useAiSearch = () => useContext(AiSearchContext);

/**
 * Shares the AI-search on/off state between the top enable-bar (which sets it),
 * the header (which swaps its search bar for the AI input when enabled) and the
 * homepage hero. Provided around the whole app layout so all three see it.
 *
 * `available` / `homeHeroAvailable` stay false during SSR and the first client
 * render (hydrated starts false), so nothing they gate can cause a hydration
 * mismatch — they reveal only after mount.
 */
export function AiSearchProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const isAiSearchRoute = router.pathname === '/rv-search';
  const isHome = router.pathname === '/';
  const flagOn = useGrowthBookFeature(FLAG, { shouldRun: isAiSearchRoute || isHome });

  const [hydrated, setHydrated] = useState(false);
  const [enabled, setEnabledState] = useState(false);
  const [forced, setForced] = useState(false);
  const [lastSearch, setLastSearch] = useState<IAiLastSearch | null>(null);

  useEffect(() => {
    setHydrated(true);
    try {
      setEnabledState(window.localStorage.getItem(STORAGE_KEY) === 'true');
      const stored = window.sessionStorage.getItem(LAST_SEARCH_KEY);
      if (stored) setLastSearch(JSON.parse(stored) as IAiLastSearch);
    } catch {
      /* no-op */
    }
  }, []);

  // Kept for the browser session so the heading survives a reload of /rv-search.
  const persistLastSearch = useCallback((next: IAiLastSearch | null) => {
    setLastSearch(next);
    try {
      if (next) window.sessionStorage.setItem(LAST_SEARCH_KEY, JSON.stringify(next));
      else window.sessionStorage.removeItem(LAST_SEARCH_KEY);
    } catch {
      /* no-op */
    }
  }, []);

  const recordSearch = useCallback(
    (query: string, resp: IAiSearchResponse) => {
      const fresh = resp.intent === 'fresh' || resp.intent === 'replace_all';
      const newVibes = resp.filters.vibes ?? [];
      // A refinement without vibes ("make it under $200") keeps the earlier feel.
      const vibes = fresh || newVibes.length > 0 ? newVibes : (lastSearch?.vibes ?? []);
      persistLastSearch({ query, vibes });
    },
    [lastSearch, persistLastSearch],
  );

  const clearLastSearch = useCallback(() => persistLastSearch(null), [persistLastSearch]);

  // Manual preview override, independent of GrowthBook — lets us QA the feature
  // anywhere (including production) before the experiment is created/enabled.
  // ?aiSearch=1 turns it on, ?aiSearch=0 turns it off; the choice sticks for the
  // browser session so it survives the homepage → /rv-search redirect, which
  // drops the query param. It only reveals the UI to whoever sets it — the
  // extractor endpoint is partner-key gated server-side.
  useEffect(() => {
    try {
      const override = new URLSearchParams(window.location.search).get('aiSearch');
      if (override === '1') window.sessionStorage.setItem(FORCE_KEY, '1');
      else if (override === '0') window.sessionStorage.removeItem(FORCE_KEY);
      setForced(window.sessionStorage.getItem(FORCE_KEY) === '1');
    } catch {
      /* no-op */
    }
  }, [router.asPath]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* no-op */
    }
  }, []);

  const flagOrForced = flagOn === true || forced;
  const available = hydrated && isAiSearchRoute && flagOrForced;
  const homeHeroAvailable = hydrated && isHome && flagOrForced;

  return (
    <AiSearchContext.Provider
      value={{
        available,
        homeHeroAvailable,
        enabled,
        setEnabled,
        lastSearch,
        recordSearch,
        clearLastSearch,
      }}>
      {children}
    </AiSearchContext.Provider>
  );
}
